import { z } from 'zod';

/**
 * Schemas for the Hopti → Doorly Pub/Sub push payloads. Mirrors the DTOs
 * in `hipto-crm-module/src/hopti-sync/dto/*` so we can keep the two
 * consumers aligned without depending on the CRM package directly.
 *
 * The push subscription wraps every message in the standard Pub/Sub
 * envelope; we always validate that first, then decode `data` (base64)
 * and parse it against the inner `HoptiIncomingLeadSyncSchema` /
 * `HoptiDeliveredLeadSyncSchema` schemas.
 */

/** Standard Pub/Sub HTTP push envelope. */
export const PubSubPushEnvelopeSchema = z.object({
  message: z.object({
    /** Base64-encoded JSON. */
    data: z.string(),
    messageId: z.string().optional(),
    publishTime: z.string().optional(),
    attributes: z.record(z.string(), z.string()).optional(),
  }),
  subscription: z.string().optional(),
});
export type PubSubPushEnvelope = z.infer<typeof PubSubPushEnvelopeSchema>;

const EventTypeEnum = z.enum(['created', 'updated', 'deleted']);

/** Inner schema for `hopti-incoming-lead-sync`. */
export const HoptiIncomingLeadSyncSchema = z
  .object({
    event_type: EventTypeEnum,
    incoming_lead_id: z.number().int(),
    timestamp: z.string(),
    data: z
      .object({
        id: z.number().int(),
        external_id: z.string().nullable().optional(),
        status: z.string(),
        status_reason: z.string().nullable().optional(),
        /** Raw lead payload as we sent it through Collect — contains
         *  `doorly_lead_id` we injected so we can pivot back. */
        data: z.record(z.string(), z.unknown()),
        email: z.string().nullable().optional(),
        mobile_phone: z.string().nullable().optional(),
        customer_id: z.string().nullable().optional(),
        account_id: z.number().nullable().optional(),
        utm_source: z.string().nullable().optional(),
        utm_campaign: z.string().nullable().optional(),
      })
      .passthrough(),
  })
  .passthrough();
export type HoptiIncomingLeadSync = z.infer<typeof HoptiIncomingLeadSyncSchema>;

/** Inner schema for `hopti-delivered-lead-sync`. */
export const HoptiDeliveredLeadSyncSchema = z
  .object({
    event_type: EventTypeEnum,
    delivery_id: z.number().int(),
    delivered_lead_id: z.string(),
    timestamp: z.string(),
    data: z
      .object({
        id: z.string(),
        delivery_id: z.number().int(),
        /** pending | success | failed | no_webservice */
        status: z.string(),
        delivery_data: z.record(z.string(), z.unknown()).nullable().optional(),
        delivered_at: z.string().nullable().optional(),
        attempts: z.number().int(),
        last_error: z.string().nullable().optional(),
        sell_price: z.number().nullable().optional(),
        is_from_stock: z.boolean(),
        incoming_lead_id: z.number().int(),
        customer_id: z.string().nullable().optional(),
        account_id: z.number().nullable().optional(),
      })
      .passthrough(),
  })
  .passthrough();
export type HoptiDeliveredLeadSync = z.infer<typeof HoptiDeliveredLeadSyncSchema>;
